import React from 'react'
import { Box, colors, Flex, space, Table, TableRow, TertiaryButton, TextInput, Tooltip } from '@workday/canvas-kit-react'
import SectionHeader from './SectionHeader'
import AlertCardContainer from './AlertCardContainer'
import IncidentCardContainer from './IncidentCardContainer'
import TimeDisplayOption from '../models/TimeDisplayOptions'
import { EmptyState } from '../../components/EmptyState'
import { AgencyModel } from '../../models/AgencyModel'
import { IncidentFeedModel } from '../../models/IncidentFeedModel'
import { IncidentModel } from '../../models/IncidentModel'
import { AlertModel } from '../../models/AlertModel'
import styled from '@emotion/styled'

const StyledTextInput = styled(TextInput)`
  padding-left: 40px;    
  background: url('/images/search-icon.png') no-repeat 2px;
  background-size: 36px;
  margin-right: 6px;
`

export type IncidentListProps = {
  timeDisplayOption: TimeDisplayOption
  collapsedHeader: boolean
  agencies: AgencyModel[]
  feedData: IncidentFeedModel
  onSelectIncident: (incident: IncidentModel) => void
  search: string
  setSearch: (search: string) => void
  width: string
  onShowUserLocation: (location: {lat:number, lng: number}) => void
}

const IncidentList = ({
  timeDisplayOption,
  collapsedHeader,
  agencies,
  feedData,
  onSelectIncident,
  search,
  setSearch,
  width,
  onShowUserLocation,
}: IncidentListProps) => {
  const alerts = feedData.alerts
  const active = feedData.active
  const recent = feedData.recent

  const agencyForAgencyID = (agencyID: string) => {
    return agencies.find(agency => agency.agencyID === agencyID)!
  }

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          
          onShowUserLocation(pos)
        }
      )
    }
  }

  return (
    <Box overflowY="auto" backgroundColor={colors.frenchVanilla100} width={width}>
      {!collapsedHeader && 
        <Box padding={space.xs}>
          <Flex>
            <StyledTextInput
              value={search}
              onChange={(event: any) => setSearch(event.currentTarget.value)}
              grow
              placeholder="Filter by address or units"
            />
            <Tooltip title="View My Location">
              <TertiaryButton as={'a'} onClick={getUserLocation}>
                <img src="/images/my-location.svg" width="24px" />
              </TertiaryButton>
            </Tooltip>
          </Flex>
        </Box>
      }
      {feedData.hasData() ? (
        <Table style={{ borderLeft: `1px solid ${colors.soap600}`, borderRight: `1px solid ${colors.soap600}` }}>
          <tbody>
            {alerts.length > 0 && <SectionHeader title="Alerts" count={alerts.length} />}
            {alerts.map((alert: AlertModel) => (
              <TableRow onClick={event => {}} key={alert.recordID}>
                <td style={{ border: 0, padding: 0, margin: 0 }}>
                  <AlertCardContainer
                    alert={alert}
                    agency={agencyForAgencyID(alert.agencyID)}
                    timeDisplayOption={timeDisplayOption}
                  />
                </td>
              </TableRow>
            ))}
            {active.length > 0 && <SectionHeader title="Active" count={active.length} />}
            {active.map((incident: IncidentModel) => {
              const agency = agencyForAgencyID(incident.agencyID)
              return (
                <TableRow 
                  style={{cursor: 'pointer'}} 
                  onClick={event => onSelectIncident(incident)} 
                  key={incident.recordID}
                >
                  <td style={{ border: 0, padding: 0, margin: 0 }}>
                    <IncidentCardContainer incident={incident} showAgencyTitle={agencies.length !== 1} agency={agency} timeDisplayOption={timeDisplayOption} />
                  </td>
                </TableRow>
              )
            })}
            {recent.length > 0 && <SectionHeader title="Recent" count={recent.length} />}
            {recent.map((incident: IncidentModel) => {
              const agency = agencyForAgencyID(incident.agencyID)
              return (
                <TableRow 
                  style={{cursor: 'pointer'}} 
                  onClick={event => onSelectIncident(incident)} 
                  key={incident.recordID}
                >
                  <td style={{ border: 0, padding: 0, margin: 0 }}>
                    <IncidentCardContainer incident={incident} agency={agency} showAgencyTitle={agencies.length !== 1} timeDisplayOption={timeDisplayOption} />
                  </td>
                </TableRow>
              )
            })}
          </tbody>
        </Table>
      ) : (
        <EmptyState>
          <EmptyState.Body>No Recent Incidents</EmptyState.Body>
        </EmptyState>
      )}
    </Box>
  )
}

export default IncidentList
